import { Archive, ArchiveRestore, ChevronDown, Star, StarOff, Tag, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface BulkActionToolbarProps {
  selectedCount: number;
  maxSelection: number;
  isProcessing: boolean;
  viewingArchived: boolean;
  onDelete: () => void;
  onArchive: (archive: boolean) => void;
  onFavourite: (isFavorite: boolean) => void;
  onAssignCategory: () => void;
  onClearSelection: () => void;
  // The pane that actually scrolls — used as the IntersectionObserver root so "stuck" is
  // detected relative to the pane's top edge (where the bar pins), not the browser viewport.
  scrollContainerRef?: React.RefObject<HTMLElement>;
}

export const BulkActionToolbar = ({
  selectedCount,
  maxSelection,
  isProcessing,
  viewingArchived,
  onDelete,
  onArchive,
  onFavourite,
  onAssignCategory,
  onClearSelection,
}: BulkActionToolbarProps) => {
  if (selectedCount === 0) return null;

  return (
    <>
      {/* Flat, full-bleed action bar that reads as part of the header chrome. It sticks to
          the pane's top edge on scroll (sm:-top-6 cancels the pane's sm:pt-6 top padding so
          it pins flush; -mx-6 cancels the pane's px-6 so it spans edge-to-edge). No border,
          rounding or shadow — same visual level as the header, not a floating card. */}
      <div className="sticky top-0 sm:-top-6 z-30 mb-4 -mx-6 flex flex-wrap items-center justify-between gap-3 border-0 bg-gray-800 px-6 py-3 sm:py-4">
      <div className="flex items-center gap-2 text-sm text-white">
        <span className="font-semibold">{selectedCount}</span>
        <span className="text-gray-300">
          post{selectedCount === 1 ? '' : 's'} selected
          {selectedCount >= maxSelection ? ` (limit ${maxSelection} reached)` : ''}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={isProcessing}
          onClick={onAssignCategory}
          className="flex items-center gap-2 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
        >
          <Tag className="h-4 w-4" />
          Assign Category
        </Button>

        <Button
          variant="outline"
          size="sm"
          disabled={isProcessing}
          onClick={() => onArchive(!viewingArchived)}
          className="flex items-center gap-2 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
        >
          {viewingArchived ? (
            <>
              <ArchiveRestore className="h-4 w-4" />
              Unarchive
            </>
          ) : (
            <>
              <Archive className="h-4 w-4" />
              Archive
            </>
          )}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isProcessing}
              className="flex items-center gap-2 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
            >
              <Star className="h-4 w-4" />
              Favourite
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-gray-700 border-gray-600">
            <DropdownMenuItem
              onClick={() => onFavourite(true)}
              className="text-white hover:bg-gray-600 cursor-pointer"
            >
              <Star className="h-4 w-4 mr-2" />
              Mark as favourite
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onFavourite(false)}
              className="text-white hover:bg-gray-600 cursor-pointer"
            >
              <StarOff className="h-4 w-4 mr-2" />
              Unmark favourite
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="outline"
          size="sm"
          disabled={isProcessing}
          onClick={onDelete}
          className="flex items-center gap-2 bg-gray-700 border-gray-600 text-red-400 hover:bg-red-900/20 hover:text-red-300"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>

        <Button
          variant="ghost"
          size="sm"
          disabled={isProcessing}
          onClick={onClearSelection}
          className="flex items-center gap-2 bg-transparent border-gray-600 text-gray-300 hover:text-white hover:bg-gray-700"
        >
          <X className="h-4 w-4" />
          Clear selection
        </Button>
      </div>
      </div>
    </>
  );
};

export default BulkActionToolbar;
